import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { handleLoginRequired } from './liveEntryGuard';
import { useOneliveSession } from './useOneliveSession';

type ConsultMessage = {
  id: number;
  nickname: string;
  message: string;
  message_type?: string;
  image_url?: string | null;
  sent_at?: string;
};

type ConsultRoomState = {
  room?: { id: number; host_nickname?: string; host_image?: string };
  settings?: { agent_name?: string; agent_image?: string; ring_duration_sec?: number };
  unread_count?: number;
  messages?: ConsultMessage[];
};

const getApi = () => (window as { G7Core?: { api?: {
  get: (url: string) => Promise<unknown>;
  post: (url: string, data?: unknown) => Promise<unknown>;
} } }).G7Core?.api;

function parseRoomState(res: unknown): ConsultRoomState {
  if (!res || typeof res !== 'object') return {};
  const layer = (res as Record<string, unknown>).data;
  return layer && typeof layer === 'object' ? (layer as ConsultRoomState) : {};
}

function parseUnread(res: unknown): number {
  if (!res || typeof res !== 'object') return 0;
  const layer = (res as Record<string, unknown>).data;
  if (!layer || typeof layer !== 'object') return 0;
  return Math.max(0, Number((layer as Record<string, unknown>).unread_count ?? 0));
}

function playConsultChime() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  } catch {
    // ignore
  }
}

function ConsultFabIcon() {
  return (
    <svg className="onelive-consult-fab__icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M24 9C14.6 9 7 15.1 7 22.5c0 4.5 2.4 8.5 6.2 11.1L11 39l7.1-4.6c1.8.5 3.8.8 5.9.8 9.4 0 17-6.1 17-13.5S33.4 9 24 9Z"
        fill="#fff"
      />
      <path
        d="M18 22.5c0 3 2.7 5.5 6 5.5s6-2.5 6-5.5"
        stroke="#7B42F6"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function ConsultCameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 4h6l1.2 2H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2.8L9 4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export default function OneliveNativeConsultWidget({
  enabled,
  agentName,
  agentImage,
  ringDurationSec = 8,
}: {
  enabled: boolean;
  agentName: string;
  agentImage: string;
  ringDurationSec?: number;
}) {
  const { user, authReady } = useOneliveSession();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState<ConsultMessage[]>([]);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [displayName, setDisplayName] = useState(agentName);
  const [displayImage, setDisplayImage] = useState(agentImage);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastUnreadRef = useRef(0);
  const ringDurationRef = useRef(ringDurationSec);

  useEffect(() => {
    ringDurationRef.current = ringDurationSec;
    setDisplayName(agentName);
    setDisplayImage(agentImage);
  }, [agentName, agentImage, ringDurationSec]);

  const loadRoom = useCallback(async (markRead = false) => {
    const api = getApi();
    if (!api || !user) return;
    const res = await api.get('/modules/onelive-chat/consult/room');
    const state = parseRoomState(res);
    const id = Number(state.room?.id ?? 0);
    setRoomId(id || null);
    setMessages(Array.isArray(state.messages) ? state.messages : []);
    if (state.settings?.agent_name) setDisplayName(state.settings.agent_name);
    if (state.settings?.agent_image) setDisplayImage(state.settings.agent_image);
    const nextUnread = Math.max(0, Number(state.unread_count ?? 0));
    if (!open && lastUnreadRef.current > 0 && nextUnread > lastUnreadRef.current) {
      playConsultChime();
    }
    lastUnreadRef.current = nextUnread;
    setUnread(nextUnread);
    if (markRead && id) {
      await api.post(`/modules/onelive-chat/consult/rooms/${id}/acknowledge`);
      setUnread(0);
      lastUnreadRef.current = 0;
    }
  }, [open, user]);

  const pollUnread = useCallback(async () => {
    const api = getApi();
    if (!api || !user) return;
    try {
      const res = await api.get('/modules/onelive-chat/consult/notifications');
      const nextUnread = parseUnread(res);
      if (!open && lastUnreadRef.current > 0 && nextUnread > lastUnreadRef.current) {
        playConsultChime();
      }
      lastUnreadRef.current = nextUnread;
      setUnread(nextUnread);
    } catch {
      // ignore
    }
  }, [open, user]);

  useEffect(() => {
    if (!enabled || !authReady || !user) return;
    void pollUnread();
    const timer = window.setInterval(() => { void pollUnread(); }, 5000);
    return () => window.clearInterval(timer);
  }, [authReady, enabled, pollUnread, user]);

  useEffect(() => {
    if (!open || !user) return;
    void loadRoom(true);
    const timer = window.setInterval(() => { void loadRoom(false); }, 4000);
    return () => window.clearInterval(timer);
  }, [loadRoom, open, user]);

  const onToggle = () => {
    if (!authReady) return;
    if (!user) {
      handleLoginRequired({
        loginRequired: '로그인후 이용해주세요',
        enterBlocked: '코인 및 등급 부족하여 입장할수 없습니다',
        broadcastPending: '관리자 승인후 방송가능 합니다.',
      }, window.location.pathname);
      return;
    }
    setOpen((prev) => !prev);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!roomId || !input.trim() || sending) return;
    setSending(true);
    try {
      const api = getApi();
      if (!api) return;
      await api.post(`/modules/onelive-chat/consult/rooms/${roomId}/messages`, { message: input.trim() });
      setInput('');
      await loadRoom(false);
    } finally {
      setSending(false);
    }
  };

  const onPickImage = () => fileRef.current?.click();

  const onImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !roomId || uploadingImage) return;

    setUploadingImage(true);
    try {
      const api = getApi();
      if (!api) return;
      const body = new FormData();
      body.append('image', file);
      await api.post(`/modules/onelive-chat/consult/rooms/${roomId}/messages/image`, body);
      await loadRoom(false);
    } finally {
      setUploadingImage(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      <div className="onelive-consult-fab-wrap">
        <span className="onelive-consult-fab__label">고객센터</span>
        <button type="button" className="onelive-consult-fab" onClick={onToggle} aria-label="고객센터">
          <ConsultFabIcon />
          {unread > 0 ? (
            <span className="onelive-consult-fab__badge">{unread > 99 ? '99+' : unread}</span>
          ) : null}
        </button>
      </div>

      {open ? (
        <div className="onelive-consult-panel" role="dialog" aria-label="고객상담">
          <header className="onelive-consult-panel__head">
            <img src={displayImage || '/img/no_profile.svg'} alt="" onError={(e) => { e.currentTarget.src = '/img/no_profile.svg'; }} />
            <div>
              <strong>{displayName}</strong>
              <span>고객상담</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="닫기">×</button>
          </header>
          <div className="onelive-consult-panel__body">
            {messages.length === 0 ? <p className="onelive-consult-panel__empty">메시지를 입력해 주세요.</p> : null}
            {messages.map((msg) => {
              const isAgent = msg.message_type === 'agent' || msg.message_type === 'system' || msg.nickname === displayName;
              const bubble = (msg.message_type === 'image' || msg.image_url)
                ? <img src={msg.image_url || ''} alt="" className="onelive-consult-panel__bubble-image" />
                : msg.message;
              return (
                <div key={msg.id} className={`onelive-consult-panel__msg${isAgent ? ' is-agent' : ' is-user'}`}>
                  <div className="onelive-consult-panel__bubble">{bubble}</div>
                </div>
              );
            })}
          </div>
          <form className="onelive-consult-panel__footer" onSubmit={(e) => void onSubmit(e)}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="메시지를 입력하세요..." maxLength={500} />
            <button type="button" className="onelive-consult-panel__img-btn" onClick={onPickImage} disabled={uploadingImage || !roomId} aria-label="이미지 전송">
              <ConsultCameraIcon />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onImageSelected(e)} />
            <button type="submit" className="onelive-consult-panel__send-btn" disabled={sending || !input.trim()}>전송</button>
          </form>
        </div>
      ) : null}

      <style>{`
        .onelive-consult-fab-wrap {
          position: fixed;
          right: 20px;
          bottom: 88px;
          z-index: 1200;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .onelive-consult-fab__label {
          font-size: 12px;
          font-weight: 700;
          color: #5b21b6;
          letter-spacing: -0.02em;
          line-height: 1;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
          user-select: none;
        }
        .onelive-consult-fab {
          position: relative;
          width: 64px;
          height: 64px;
          border: 0;
          border-radius: 22px;
          background: #7b42f6;
          box-shadow: 0 10px 24px rgba(123, 66, 246, 0.38);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .onelive-consult-fab:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(123, 66, 246, 0.42);
        }
        .onelive-consult-fab:active {
          transform: translateY(0);
        }
        .onelive-consult-fab__icon {
          width: 38px;
          height: 38px;
          display: block;
        }
        .onelive-consult-fab__badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 20px;
          height: 20px;
          padding: 0 5px;
          border-radius: 999px;
          background: #ef4444;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #fff;
        }
        .onelive-consult-panel {
          position: fixed;
          right: 20px;
          bottom: 178px;
          z-index: 1201;
          width: min(360px, calc(100vw - 32px));
          height: 460px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .onelive-consult-panel__head {
          display: grid;
          grid-template-columns: 44px 1fr auto;
          gap: 10px;
          align-items: center;
          padding: 14px 16px;
          background: #f5f3ff;
          border-bottom: 1px solid #ede9fe;
        }
        .onelive-consult-panel__head img {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          object-fit: cover;
        }
        .onelive-consult-panel__head strong { display: block; font-size: 15px; }
        .onelive-consult-panel__head span { color: #6b7280; font-size: 12px; }
        .onelive-consult-panel__head button {
          border: 0;
          background: transparent;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          color: #6b7280;
        }
        .onelive-consult-panel__body {
          flex: 1;
          overflow: auto;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #fafafa;
        }
        .onelive-consult-panel__empty { color: #9ca3af; font-size: 13px; text-align: center; margin: auto; }
        .onelive-consult-panel__msg { display: flex; }
        .onelive-consult-panel__msg.is-agent { justify-content: flex-start; }
        .onelive-consult-panel__msg.is-user { justify-content: flex-end; }
        .onelive-consult-panel__bubble {
          max-width: 80%;
          padding: 10px 12px;
          border-radius: 14px;
          font-size: 14px;
          line-height: 1.45;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .onelive-consult-panel__msg.is-agent .onelive-consult-panel__bubble {
          background: #fff;
          border: 1px solid #e5e7eb;
        }
        .onelive-consult-panel__msg.is-user .onelive-consult-panel__bubble {
          background: #7c3aed;
          color: #fff;
        }
        .onelive-consult-panel__bubble-image {
          display: block;
          max-width: 220px;
          max-height: 220px;
          border-radius: 10px;
          object-fit: cover;
        }
        .onelive-consult-panel__footer {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          gap: 8px;
          padding: 12px;
          border-top: 1px solid #e5e7eb;
          align-items: center;
        }
        .onelive-consult-panel__footer input {
          border: 1px solid #d1d5db;
          border-radius: 10px;
          padding: 10px 12px;
          min-width: 0;
        }
        .onelive-consult-panel__img-btn {
          width: 40px;
          height: 40px;
          border: 0;
          border-radius: 10px;
          background: #6b7280;
          color: #fff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .onelive-consult-panel__img-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .onelive-consult-panel__send-btn {
          border: 0;
          border-radius: 10px;
          background: #7c3aed;
          color: #fff;
          min-width: 52px;
          height: 40px;
          padding: 0 14px;
          cursor: pointer;
          white-space: nowrap;
          writing-mode: horizontal-tb;
          flex-shrink: 0;
          font-size: 14px;
          font-weight: 600;
        }
        .onelive-consult-panel__send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .onelive-consult-fab-wrap { right: 14px; bottom: 78px; }
          .onelive-consult-panel { right: 14px; bottom: 168px; height: 420px; }
        }
      `}</style>
    </>
  );
}
